import React, { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import EvolutieBarChart, { fmtMii } from "./EvolutieBarChart";
import { sel } from "./configuratorMetrics";

const GRANS = [["an", "Ani"], ["semestru", "Semestre"], ["trimestru", "Trimestre"], ["luna", "Luni"], ["saptamana", "Saptamani"], ["zi", "Zile"]];
const TIPURI = [["toti", "Toti"], ["pf", "Persoana fizica"], ["pj", "Persoana juridica"], ["nedefinit", "Nedefinit"]];
const SERII = [["total", "Clienti noi", "#0f172a"], ["pf", "Persoana fizica", "#2563eb"], ["pj", "Persoana juridica", "#c08a00"], ["cumulat", "Cumulat", "#16a34a"]];

export default function EvolutieClienti({ endpoint, testid }) {
  const [f, setF] = useState({ date_from: "", date_to: "", granularity: "an", tip: "toti" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if ((f.date_from && !f.date_to) || (!f.date_from && f.date_to)) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoint, { params: f });
      setData(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      setLoading(false);
    }
  }, [f, endpoint]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const serii = f.tip === "toti" ? SERII : SERII.filter(([k]) => k === "total" || k === "cumulat");

  return (
    <div data-testid={testid}>
      <div className="mb-5 flex flex-wrap items-center justify-end gap-3 text-sm">
        <label className="flex items-center gap-2">Tip client
          <select data-testid={`${testid}-filter-tip`} className={sel} value={f.tip} onChange={set("tip")}>
            {TIPURI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">Granularitate
          <select data-testid={`${testid}-filter-granularity`} className={sel} value={f.granularity} onChange={set("granularity")}>
            {GRANS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-2">De la
          <input data-testid={`${testid}-filter-date-from`} type="date" className={sel} value={f.date_from} onChange={set("date_from")} />
        </label>
        <label className="flex items-center gap-2">Pana la
          <input data-testid={`${testid}-filter-date-to`} type="date" className={sel} value={f.date_to} onChange={set("date_to")} />
        </label>
      </div>

      {loading && <div data-testid={`${testid}-loading`} className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Se incarca...</div>}
      {error && <div data-testid={`${testid}-error`} className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {data && !error && (
        <>
          <Sumar data={data} testid={testid} />
          <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {serii.map(([k, l]) => (
              <div key={k} data-testid={`${testid}-chart-${k}`} className="rounded border border-slate-200 bg-white p-4 shadow-sm">
                <div className="mb-2 text-sm font-medium text-slate-700">{l}</div>
                <EvolutieBarChart labels={data.labels || []} values={data.series?.[k] || []} />
              </div>
            ))}
          </div>
          <TabelEvolutie rows={data.rows || []} totals={data.totals || {}} testid={testid} />
        </>
      )}
    </div>
  );
}

function Sumar({ data, testid }) {
  const t = data.totals || {};
  const items = [["Total", t.total], ["PF", t.pf], ["PJ", t.pj], ["Nedefinit", t.nedefinit], ["Fara data introducere (exclusi)", data.fara_data]];
  return (
    <div data-testid={`${testid}-sumar`} className="mb-4 flex flex-wrap gap-3 text-sm">
      {items.map(([l, v]) => (
        <div key={l} className="rounded border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <span className="text-slate-500">{l}: </span><b className="text-slate-800">{fmtMii(v)}</b>
        </div>
      ))}
      {data.date_from && <div className="self-center text-xs text-slate-500">Interval: {data.date_from} - {data.date_to}</div>}
      {data.dedup && <Dedup d={data.dedup} testid={testid} />}
    </div>
  );
}

function Dedup({ d, testid }) {
  return (
    <div data-testid={`${testid}-dedup`} className="w-full text-xs text-slate-500">
      Surse: ERP-vechi {fmtMii(d.erp_vechi)} + ERP-nou {fmtMii(d.erp_nou)}. Clienti unici: {fmtMii(d.unici)}. Dubluri eliminate: {fmtMii(d.dubluri_eliminate)} (prezenti in ambele ERP: {fmtMii(d.in_ambele)}). Fara CUI/CNP si fara nr. contract (nededuplicati): {fmtMii(d.fara_cheie)}.
    </div>
  );
}

function TabelEvolutie({ rows, totals, testid }) {
  const cols = [["pf", "PF"], ["pj", "PJ"], ["nedefinit", "Nedefinit"], ["total", "Total"], ["cumulat", "Cumulat"]];
  return (
    <div className="max-h-[480px] overflow-auto rounded border border-slate-200 bg-white shadow-sm">
      <table data-testid={`${testid}-table`} className="w-full text-sm">
        <thead className="sticky top-0 bg-slate-50 text-left text-slate-600">
          <tr><th className="px-3 py-2">Perioada</th>{cols.map(([k, l]) => <th key={k} className="px-3 py-2 text-right">{l}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.start} className="border-t border-slate-100">
              <td className="px-3 py-1.5">{r.perioada}</td>
              {cols.map(([k]) => <td key={k} className="px-3 py-1.5 text-right tabular-nums">{fmtMii(r[k])}</td>)}
            </tr>
          ))}
          <tr className="border-t-2 border-slate-300 font-semibold">
            <td className="px-3 py-2">Total</td>
            {cols.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums">{k === "cumulat" ? "" : fmtMii(totals[k])}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
